import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, styled, TextField } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-premium';

import useSettings from 'app/main/components/hooks/useSettings';

import { type ModuleSearchFieldProps } from 'app/main/components/fields/ModuleSearchField';
import useModuleSearch from 'app/main/components/fields/components/useModuleSearch';
import { ModuleSearchPopupIndicator } from 'app/main/components/fields/components/ModuleSearchComponents';
// eslint-disable-next-line import/no-cycle
import { showModuleSearchFieldDialog } from 'app/main/components/fields/components/ModuleSearchFieldDialog';

import type { EditInputCellParams } from '../DetailsSectionTableInputCells';

type ModuleSearchEditInputCellParams = EditInputCellParams &
	Pick<ModuleSearchFieldProps, 'module' | 'filters' | 'getOptionLabel'>;

const ModuleSearchEditInputCell = styled((props: ModuleSearchEditInputCellParams) => {
	const dispatch = useDispatch<any>();
	const { settings, t } = useSettings(props.module);
	const apiRef = useGridApiContext();

	const inputRef = React.useRef<HTMLInputElement>(undefined);

	const isInsideAnotherDialog = useSelector(({ fuse }: any) => fuse.moduleSearchDialog.state);

	const [value, setValue] = React.useState<any>();

	const baseParseValue = newValue => ({ [props.field]: newValue, [`${settings.modu_short}_title`]: newValue });

	const {
		options,
		isLoading,
		PaperComponent,
		isOptionEqualToValue,
		parseOptionLabel,
		renderOption,
		handleInputChange,
		handleKeyDown
	} = useModuleSearch({
		module: props.module,
		uid: props.value,
		filters: props.filters,
		getOptionLabel: props.getOptionLabel,
		value,
		setValue
	});

	const handleChange = newValue => {
		const newRow = (props.parseValue || baseParseValue)(newValue !== null ? newValue : {});
		Object.keys(newRow).forEach(async key =>
			apiRef.current.setEditCellValue({ id: props.id, field: key, value: newRow[key] })
		);
		apiRef.current.updateRows([{ ...props.row, ...newRow, ___MODULESEARCH_DIALOG_OPEN___: false }]);
		apiRef.current.setCellFocus(props.id, props.field);
		setValue(newValue);
	};

	const handleOpenDialog = () => {
		apiRef.current.updateRows([{ ...props.row, ___MODULESEARCH_DIALOG_OPEN___: true }]);
		dispatch(
			showModuleSearchFieldDialog({
				label: props.colDef.headerName,
				module: props.module,
				filters: props.filters,
				value,
				sendChange: handleChange,
				parseOptionLabel
			})
		);
	};

	React.useEffect(() => {
		if (props.hasFocus) inputRef.current?.focus();
	}, [props.hasFocus]);

	const PopupIndicator = React.forwardRef(() => ModuleSearchPopupIndicator({ handleOpenDialog }));

	return (
		<Autocomplete
			className={props.className}
			renderInput={params => (
				<TextField
					{...params}
					inputRef={inputRef}
					name={props.field}
					variant="filled"
					InputProps={{ ...params.InputProps, disableUnderline: true }}
				/>
			)}
			slotProps={{
				paper: { component: PaperComponent },
				popupIndicator: { component: PopupIndicator }
			}}
			options={options}
			value={value || null}
			onInputChange={handleInputChange}
			onChange={(_event, newValue) => handleChange(newValue)}
			onKeyDown={handleKeyDown}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			filterOptions={() => options}
			isOptionEqualToValue={isOptionEqualToValue}
			getOptionLabel={parseOptionLabel}
			renderOption={renderOption}
			selectOnFocus
			forcePopupIcon={!isInsideAnotherDialog}
			// freeSolo
		/>
	);
})(({ theme }) => ({
	width: '100%',
	height: '100%',
	'& > .MuiTextField-root': {
		height: '100%',
		'& > .MuiInputBase-root': {
			height: '100%',
			paddingTop: 0,
			borderRadius: 0,
			'& > .MuiInputBase-input': {
				paddingLeft: 0,
				paddingRight: 0
			},
			'& > .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
				padding: 0
			}
		}
	}
}));

export default React.memo(ModuleSearchEditInputCell);
